import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../apiFetch";

const REFRESH_INTERVAL_MS = 5000;

export interface CameraProfile {
  width: number;
  height: number;
  fps: number;
  quality: number;
}

export interface CameraInfo {
  id: string;
  status: "online" | "offline" | "error";
  lastSeenAt: number;
  capabilities?: unknown;
  effectiveProfile?: CameraProfile;
  active?: boolean;
}

export interface UseCamerasResult {
  cameras: CameraInfo[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useCameras(): UseCamerasResult {
  const [cameras, setCameras] = useState<CameraInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await apiFetch("/api/cameras");
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = (await res.json()) as { cameras?: CameraInfo[] };
        if (!cancelled) {
          setCameras(data.cameras ?? []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(String(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [reloadKey]);

  return { cameras, loading, error, refresh };
}

export async function updateCameraProfile(
  cameraId: string,
  profile: Partial<CameraProfile>,
): Promise<{ ok: true; profile: CameraProfile } | { ok: false; error: string }> {
  try {
    const res = await apiFetch(`/api/cameras/${encodeURIComponent(cameraId)}/profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: body.error ?? `HTTP ${res.status}` };
    }
    const body = (await res.json()) as { profile: CameraProfile };
    return { ok: true, profile: body.profile };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}
